"use client";

import React, { createContext, useState, useEffect, useCallback, useRef } from "react";
import cookie, { set } from "js-cookie";

// Type imports only - no runtime Firebase imports
type User = any; // Will be properly typed when Firebase loads
 import { usePathname, useRouter } from "next/navigation";
import { HubConnection } from "@microsoft/signalr";
import { socketService } from "@/lib/MemoBE/Message";
import { AppContextType, UserInfoType } from "@/interFace/general";
import { buildUserInfo, EMPTY_USER_INFO } from "@/lib/userInfo";
import { isUserVerified } from "@/lib/MemoBE/Member";
import { searchBookings } from "@/lib/MemoBE/Bookings";
import { toast } from "react-toastify";
import { answerType } from "@/interFace/courses/types";
import {
  BackendUserType,
  Message,
  ScoreMessage,
  Race,
  PlayerType,
  PushNotificationTypeEnum,
} from "@/interFace/inbox/types";
import { BookingScope, BookingStatus, BookingDto } from "@/interFace/bookings/types";
import { getFirebaseAuth } from "@/config/firebase/firebaseClient";
import { fetchWithAuth } from "@/lib/MemoBE/fetchWithAuth";
import { ContactStatusEnum, Friendship } from "@/interFace/inbox/types";
import { routing } from "@/i18n/routing";
import { canonicalNotificationHint, notificationHistoryApi, NotificationHistoryPage } from "@/lib/MemoBE/Notifications";

interface AppProviderProps {
  children: React.ReactNode;
  culture: string;
}

export const AppContext = createContext<AppContextType | undefined>(undefined);
const tokenName = "firebaseToken";

const UPCOMING_BOOKING_NOTIFICATION_WINDOW_MONTHS = 12;

const getUpcomingBookingWindow = () => {
  const now = new Date();
  const to = new Date(now);
  to.setMonth(to.getMonth() + UPCOMING_BOOKING_NOTIFICATION_WINDOW_MONTHS);

  return {
    fromUtc: now.toISOString(),
    toUtc: to.toISOString(),
  };
};

const isPendingBookingNotification = (booking: BookingDto, currentUserId?: string | null) => {
  if (!currentUserId) {
    return false;
  }

  const isParticipant = booking.participantUserIds?.includes(currentUserId);
  const hasApproved = booking.approvedParticipantUserIds?.includes(currentUserId);
  const hasDeclined = booking.declinedParticipantUserIds?.includes(currentUserId);
  const isFutureBooking = new Date(booking.startUtc).getTime() > Date.now();

  return Boolean(
    booking.scopeId === BookingScope.Personal &&
    booking.createdByUserId !== currentUserId &&
    isParticipant &&
    isFutureBooking &&
    booking.statusId !== BookingStatus.Cancelled &&
    booking.statusId !== BookingStatus.Declined &&
    !hasApproved &&
    !hasDeclined
  );
};

const AppProvider = ({ children, culture }: AppProviderProps) => {
  const [sideMenuOpen, setSideMenuOpen] = useState<boolean>(false);
  const [headerSerachOpen, setHeaderSerachOpen] = useState<boolean>(false);
  const [scrollDirection, setScrollDirection] = useState("up");
  const [inputValue, setInputValue] = useState<string>("");
  const [filterType, setFilterType] = useState<string>("");
  const [confirmationPage, setConfirmationPage] = useState<boolean>(true);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [authResolved, setAuthResolved] = useState(false);
  const [userInfoResolved, setUserInfoResolved] = useState(false);
  const [sessionRevision, setSessionRevision] = useState(0);
  const [userInfo, setUserInfo] = useState<UserInfoType>(EMPTY_USER_INFO);
  const [locale, setLocale] = useState<string>(culture);
  const [inboxConnection, setInboxConnection] = useState<HubConnection | null>(
    null
  );
  const inboxConnectionRef = useRef<HubConnection | null>(null);
  const currentUserRef = useRef<User | null>(null);
  const authGeneration = useRef(0);
  const userInfoRequest = useRef(0);
  const [notificationProjection, setNotificationProjection] = useState<{ owner: string; page: NotificationHistoryPage | null; error: boolean } | null>(null);
  const notificationRequest = useRef(0);
  const notificationOwnerRef = useRef<string | null>(null);
  notificationOwnerRef.current = currentUser?.uid ?? null;
  const [playTime, setPlayTime] = useState<number>(0);
  const [playAnswers, setPlayAnswers] = useState<answerType[]>([]);
  const [playStartTime, setPlayStartTime] = useState<Date | null>(null);
  const [playEndTime, setPlayEndTime] = useState<Date | null>(null);
  const [newMessage, setNewMessage] = useState<Message | null>(null);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [invitationCount, setInvitationCount] = useState<number>(0);
  const [bookingNotificationCount, setBookingNotificationCount] = useState<number>(0);
  const [newContactUserIds, setNewContactUserIds] = useState<string[]>([]);
  const [challengeCount, setChallengeCount] = useState<number>(0);
  const [numberOfBattlePlayers, setNumberOfBattlePlayers] = useState<number>(0);
  const [battleScore, setBattleScore] = useState<ScoreMessage[]>([]);
  const [startBattle, setStartBattle] = useState<boolean>(false);
  const router = useRouter();
  const pathname = usePathname();
  const pathSegments = pathname?.split("/").filter(Boolean) ?? [];
  const pathLocale = pathSegments[0] && routing.locales.includes(pathSegments[0] as any)
    ? pathSegments[0]
    : null;
  const isPlayRoute = pathSegments[0] === "play" || pathSegments[1] === "play";

  useEffect(() => {
    if (pathLocale && pathLocale !== locale) {
      setLocale(pathLocale);
    }
  }, [locale, pathLocale]);

  const resetUnreadCount = () => {
    setUnreadCount(0);
  };

  const refreshNotificationHistory = useCallback(async () => {
    const owner = notificationOwnerRef.current;
    if (!owner) return;
    const request = ++notificationRequest.current;
    try {
      const page = await notificationHistoryApi.list();
      if (request !== notificationRequest.current || notificationOwnerRef.current !== owner) return;
      if (!Array.isArray(page.items) || page.items.some(item => item.recipientUserId !== owner || item.schemaVersion !== 1)) throw new Error("Invalid history");
      setNotificationProjection({ owner, page, error: false });
    } catch {
      if (request === notificationRequest.current && notificationOwnerRef.current === owner)
        setNotificationProjection(previous => ({ owner, page: previous?.owner === owner ? previous.page : null, error: true }));
    }
  }, []);

  const ownNotificationProjection = notificationProjection?.owner === currentUser?.uid ? notificationProjection : null;
  const readNotification = useCallback(async (id: string) => {
    const owner = notificationOwnerRef.current;
    if (!owner || !/^[a-f0-9]{64}$/i.test(id)) return null;
    try {
      const item = await notificationHistoryApi.detail(id);
      if (notificationOwnerRef.current !== owner || item.recipientUserId !== owner || item.notificationId !== id || item.schemaVersion !== 1) return null;
      await notificationHistoryApi.read(id);
      if (notificationOwnerRef.current !== owner) return null;
      await refreshNotificationHistory();
      return item;
    } catch {
      if (notificationOwnerRef.current === owner) setNotificationProjection(previous => ({ owner, page: previous?.owner === owner ? previous.page : null, error: true }));
      return null;
    }
  }, [refreshNotificationHistory]);

  const readAllNotifications = useCallback(async () => {
    const owner = notificationOwnerRef.current;
    const snapshot = notificationProjection?.owner === owner ? notificationProjection.page?.snapshot : null;
    if (!owner || !snapshot) return;
    try { await notificationHistoryApi.readAll(snapshot); if (notificationOwnerRef.current === owner) await refreshNotificationHistory(); }
    catch { if (notificationOwnerRef.current === owner) setNotificationProjection(previous => ({ owner, page: previous?.owner === owner ? previous.page : null, error: true })); }
  }, [notificationProjection, refreshNotificationHistory]);

  useEffect(() => {
    notificationRequest.current++;
    setNotificationProjection(null);
    if (!currentUser?.uid) return;
    void refreshNotificationHistory();
    const resume = () => { if (document.visibilityState === "visible") void refreshNotificationHistory(); };
    window.addEventListener("focus", resume);
    window.addEventListener("online", resume);
    document.addEventListener("visibilitychange", resume);
    return () => { notificationRequest.current++; window.removeEventListener("focus", resume); window.removeEventListener("online", resume); document.removeEventListener("visibilitychange", resume); };
  }, [currentUser?.uid, refreshNotificationHistory]);

  const resetInvitationCount = () => {
    setInvitationCount(0);
  };

  const resetBookingNotificationCount = () => {
    setBookingNotificationCount(0);
  };

  useEffect(() => {
    currentUserRef.current = currentUser;
  }, [currentUser]);

  const refreshUserInfo = useCallback(async (user?: User | null) => {
    if (!user?.uid || currentUserRef.current?.uid !== user.uid) {
      return;
    }

    const generation = authGeneration.current;
    const request = ++userInfoRequest.current;
    const isCurrent = () => authGeneration.current === generation && userInfoRequest.current === request && currentUserRef.current?.uid === user.uid;
    try {
      const token = await user.getIdToken();
      if (!isCurrent()) return;
      const {
        isVerified,
        accountTypeId,
        isTester,
        profiles,
        roleId,
        roles,
        activeRole,
        workspaces,
        defaultWorkspace,
        capabilities,
        yearOfBirth,
      } = await isUserVerified(user.uid, token);

      if (!isCurrent()) return;
      if (!isVerified) {
        setUserInfo(EMPTY_USER_INFO);
        return;
      }

      const age =
        typeof yearOfBirth === "number" ? new Date().getFullYear() - yearOfBirth : null;

      setUserInfo(previous =>
        buildUserInfo({
          roleId,
          accountTypeId,
          isTester,
          profiles,
          roles,
          activeRole: previous.activeRole ?? activeRole,
          activeWorkspace: previous.activeWorkspace,
          workspaces,
          defaultWorkspace,
          capabilities,
          age,
        })
      );
    } catch (error) {
      if (isCurrent()) setUserInfo(EMPTY_USER_INFO);
      console.error("Failed to refresh user info:", error);
    } finally {
      if (isCurrent()) setUserInfoResolved(true);
    }
  }, []);

  const resetContactCount = () => {
    setNewContactUserIds([]);
  };

  const resetChallengeCount = () => {
    setChallengeCount(0);
  };

  const refreshInvitationCount = useCallback(async (userId?: string | null) => {
    if (!userId) {
      setInvitationCount(0);
      return;
    }

    try {
      if (!currentUser?.uid) {
        return;
      }

      const response = await fetchWithAuth("/api/inbox/invitation-list", "GET");
      const invitations = Array.isArray(response) ? (response as Friendship[]) : [];
      const pendingReceivedInvitations = invitations.filter(
        (invitation) =>
          invitation.status === ContactStatusEnum.Waiting &&
          invitation.toUserId === userId
      ).length;

      let managedChildInvitationCount = 0;
      if (userInfo.workspaces.includes("parent")) {
        try {
          const managedResponse = await fetchWithAuth("/api/parents/managed-child-invitations", "GET");
          const managedInvitations = Array.isArray(managedResponse) ? managedResponse : [];
          managedChildInvitationCount = managedInvitations.length;
        } catch (error) {
          console.error("Failed to refresh managed child invitation count:", error);
        }
      }

      setInvitationCount(pendingReceivedInvitations + managedChildInvitationCount);
    } catch (error) {
      console.error("Failed to refresh invitation count:", error);
    }
  }, [currentUser?.uid, userInfo.workspaces]);

  const refreshContactNotifications = useCallback(async () => {
    try {
      if (!currentUser?.uid) {
        setNewContactUserIds([]);
        return;
      }

      const response = await fetchWithAuth("/api/inbox/user-contacts", "POST", { withSummaries: false });
      const contacts = Array.isArray(response) ? (response as BackendUserType[]) : [];
      const nextUserIds = contacts
        .filter((contact) => contact.isNew && typeof contact.userId === "string" && contact.userId.length > 0)
        .map((contact) => contact.userId);

      setNewContactUserIds([...new Set(nextUserIds)]);
    } catch (error) {
      console.error("Failed to refresh contact notifications:", error);
    }
  }, [currentUser?.uid]);

  const refreshUnreadNotifications = useCallback(async () => {
    try {
      if (!currentUser?.uid) {
        setUnreadCount(0);
        return;
      }

      const response = await fetchWithAuth(
        "/api/inbox/user-contacts",
        "POST",
        { withSummaries: true }
      );
      const contacts = Array.isArray(response) ? (response as BackendUserType[]) : [];
      const unreadConversations = contacts.filter(
        (contact) => contact.hasUnreadConversation ?? contact.hasNewMessages
      ).length;

      setUnreadCount(unreadConversations);
    } catch (error) {
      console.error("Failed to refresh unread notifications:", error);
    }
  }, [currentUser?.uid]);

  const refreshBookingNotifications = useCallback(async (userId?: string | null) => {
    if (!userId) {
      setBookingNotificationCount(0);
      return;
    }

    try {
      const response = await searchBookings({
        ...getUpcomingBookingWindow(),
        participantUserId: userId,
      });

      const pendingBookings = (response.items || []).filter((booking) =>
        isPendingBookingNotification(booking, userId)
      ).length;

      setBookingNotificationCount(pendingBookings);
    } catch (error) {
      console.error("Failed to refresh booking notifications:", error);
    }
  }, []);

  // Firebase initialization with a single auth listener so auth resolution is deterministic.
  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    let isMounted = true;
    let hasResolvedInitialAuth = false;
    let unsubscribe: (() => void) | undefined;

    const resolveAuthState = (user: User | null) => {
      if (!isMounted) {
        return;
      }

      hasResolvedInitialAuth = true;
      const ownerChanged = currentUserRef.current?.uid !== user?.uid;
      const generation = ++authGeneration.current;
      ++userInfoRequest.current;
      ++notificationRequest.current;
      currentUserRef.current = user;
      notificationOwnerRef.current = user?.uid ?? null;
      cookie.remove(tokenName);
      setUserInfoResolved(!user);
      setNotificationProjection(null);
      if (ownerChanged || !user) {
        setSessionRevision(previous => previous + 1);
        setUserInfo(EMPTY_USER_INFO);
        setPlayAnswers([]);
        setPlayTime(0);
        setPlayStartTime(null);
        setPlayEndTime(null);
        setNewMessage(null);
        setUnreadCount(0);
        setInvitationCount(0);
        setBookingNotificationCount(0);
        setNewContactUserIds([]);
        setChallengeCount(0);
        setBattleScore([]);
        setNumberOfBattlePlayers(0);
        setStartBattle(false);
      }
      setAuthResolved(true);
      setCurrentUser(user);

      if (user) {
        user
          .getIdToken()
          .then((idToken: string) => {
            if (!isMounted || authGeneration.current !== generation || currentUserRef.current?.uid !== user.uid) {
              return;
            }

            cookie.set(tokenName, idToken, { expires: 14 });
          })
          .catch((error: unknown) => {
            console.error("Failed to read Firebase ID token:", error);
          });
        void refreshUserInfo(user);
        return;
      }

      cookie.remove(tokenName);
      setUserInfo(EMPTY_USER_INFO);
    };

    const initializeFirebaseAuth = async () => {
      try {
        const auth = await getFirebaseAuth();

        if (!isMounted) {
          return;
        }

        unsubscribe = auth.onIdTokenChanged((user: User | null) => {
          resolveAuthState(user);
        });

        if (typeof (auth as any).authStateReady === "function") {
          try {
            await (auth as any).authStateReady();

            if (!hasResolvedInitialAuth) {
              resolveAuthState(auth.currentUser);
            }
          } catch (error) {
            console.error("Failed waiting for Firebase auth state:", error);
            if (!hasResolvedInitialAuth) {
              resolveAuthState(auth.currentUser);
            }
          }
        } else if (!hasResolvedInitialAuth) {
          resolveAuthState(auth.currentUser);
        }
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setAuthResolved(true);
        setUserInfoResolved(true);
        setCurrentUser(null);
        setUserInfo(EMPTY_USER_INFO);
        cookie.remove(tokenName);
        console.error("Failed to initialize Firebase Auth:", error);
      }
    };

    initializeFirebaseAuth();

    return () => {
      isMounted = false;
      ++authGeneration.current;
      ++userInfoRequest.current;
      currentUserRef.current = null;
      unsubscribe?.();
    };
  }, [refreshUserInfo]);

  // useEffect 2: Establish SignalR connection when user logs in
  useEffect(() => {
    const currentUserId = currentUser?.uid;

    // Only connect if user is authenticated
    if (!currentUserId || isPlayRoute) {
      // Disconnect if user logs out
      if (inboxConnectionRef.current) {
        socketService.stopConnection(inboxConnectionRef.current);
        inboxConnectionRef.current = null;
        setInboxConnection(null);
      }
      if (!currentUserId) {
        setInvitationCount(0);
        setBookingNotificationCount(0);
        setNewContactUserIds([]);
      }
      return;
    }

    let isDisposed = false;
    let activeConnection: HubConnection | null = null;

    const SetConnection = async () => {
      if (inboxConnectionRef.current) {
        socketService.stopConnection(inboxConnectionRef.current);
        inboxConnectionRef.current = null;
      }

      const connection = await socketService.registerOnServerEvents(
        (message) => {
          if (isDisposed || notificationOwnerRef.current !== currentUserId) return;
          const historyHint = canonicalNotificationHint(message, currentUserId);
          if (historyHint === "invalid") return;
          if (historyHint === "canonical") void refreshNotificationHistory();

          const normalizeType = (t: any): string | null => {
            if (t === undefined || t === null) return null;
            if (typeof t === 'number') return (PushNotificationTypeEnum as any)[t] ?? String(t);
            if (typeof t === 'string') {
              if (/^\d+$/.test(t)) return (PushNotificationTypeEnum as any)[Number(t)] ?? t;
              if ((PushNotificationTypeEnum as any)[t] !== undefined) return t;
              return t;
            }
            return String(t);
          };

          const typeName = normalizeType(message.type);

          if (typeName === (PushNotificationTypeEnum as any)[PushNotificationTypeEnum.NewMessage] || typeName === 'NewMessage') {
            setNewMessage(message);
            if (typeof window !== 'undefined' && !window.location.pathname.includes('/inbox')) {
              setUnreadCount(prev => prev + 1);
            }
          }

          if (typeName === (PushNotificationTypeEnum as any)[PushNotificationTypeEnum.NewBattleRequest] || typeName === 'NewBattleRequest') {
            setChallengeCount(prev => prev + 1);
          }

          if (
            typeName === (PushNotificationTypeEnum as any)[PushNotificationTypeEnum.BookingReceived] ||
            typeName === 'BookingReceived' ||
            typeName === (PushNotificationTypeEnum as any)[PushNotificationTypeEnum.BookingParticipantCancelled] ||
            typeName === 'BookingParticipantCancelled' ||
            typeName === (PushNotificationTypeEnum as any)[PushNotificationTypeEnum.BookingParticipantUpdated] ||
            typeName === 'BookingParticipantUpdated'
          ) {
            refreshBookingNotifications(currentUserId);

            if (typeof window !== 'undefined') {
              window.dispatchEvent(
                new CustomEvent('memo:booking-updated', {
                  detail: {
                    type: typeName,
                    message,
                  },
                })
              );
            }

            const bookingToastMessage =
              typeof message?.text === 'string' && message.text.trim().length > 0
                ? message.text
                : ((typeName === (PushNotificationTypeEnum as any)[PushNotificationTypeEnum.BookingParticipantUpdated] || typeName === 'BookingParticipantUpdated') && message?.action === 'accepted')
                  ? 'A participant accepted one of your meetings.'
                : ((typeName === (PushNotificationTypeEnum as any)[PushNotificationTypeEnum.BookingParticipantUpdated] || typeName === 'BookingParticipantUpdated') && message?.action === 'declined')
                  ? 'A participant declined one of your meetings.'
                  : typeName === (PushNotificationTypeEnum as any)[PushNotificationTypeEnum.BookingParticipantCancelled] || typeName === 'BookingParticipantCancelled'
                    ? 'A booking was cancelled.'
                    : 'Your bookings were updated.';

            toast.info(bookingToastMessage, { progress: undefined, autoClose: 3000 });
          }

          if (
            typeName === (PushNotificationTypeEnum as any)[PushNotificationTypeEnum.InvitationReceived] ||
            typeName === 'InvitationReceived' ||
            typeName === (PushNotificationTypeEnum as any)[PushNotificationTypeEnum.InvitationAccepted] ||
            typeName === 'InvitationAccepted' ||
            typeName === (PushNotificationTypeEnum as any)[PushNotificationTypeEnum.InvitationUpdated] ||
            typeName === 'InvitationUpdated'
          ) {
            refreshInvitationCount(currentUserId);
            refreshContactNotifications();
            refreshUnreadNotifications();
            if (typeName === (PushNotificationTypeEnum as any)[PushNotificationTypeEnum.InvitationAccepted] || typeName === 'InvitationAccepted') {
              refreshUserInfo(currentUserRef.current);
              const contactUserId = typeof message?.from === 'string' ? message.from : '';
              if (contactUserId) {
                setNewContactUserIds((prev) => prev.includes(contactUserId) ? prev : [...prev, contactUserId]);
              }

              if (typeof window !== 'undefined') {
                window.dispatchEvent(
                  new CustomEvent('memo:contact-updated', {
                    detail: {
                      type: typeName,
                      message,
                      contactUserId,
                    },
                  })
                );
              }
            }

            if (
              (typeName === (PushNotificationTypeEnum as any)[PushNotificationTypeEnum.InvitationUpdated] || typeName === 'InvitationUpdated'
                || typeName === (PushNotificationTypeEnum as any)[PushNotificationTypeEnum.InvitationReceived] || typeName === 'InvitationReceived')
              && typeof window !== 'undefined'
            ) {
              const contactUserId = typeof message?.from === 'string' ? message.from : '';
              window.dispatchEvent(
                new CustomEvent('memo:contact-updated', {
                  detail: {
                    type: typeName,
                    message,
                    contactUserId,
                  },
                })
              );
            }

            if (typeof window !== 'undefined') {
              window.dispatchEvent(
                new CustomEvent('memo:invitation-updated', {
                  detail: {
                    type: typeName,
                    message,
                  },
                })
              );
            }

            if (historyHint === "legacy" && message?.text) {
              toast.info(message.text, { progress: undefined, autoClose: 3000 });
            }
          }
        },
        (scoreMessage) => {
          setBattleScore((prevScore) => {
            if (!scoreMessage) return prevScore;

            // Find existing by id or by from+raceId
            const idx = prevScore.findIndex((s) =>
              (s.id && scoreMessage.id && s.id === scoreMessage.id) ||
              (s.from === scoreMessage.from && s.raceId === scoreMessage.raceId)
            );

            if (idx !== -1) {
              const copy = [...prevScore];
              copy[idx] = scoreMessage;
              return copy;
            }

            return [...prevScore, scoreMessage];
          });
        },
        (command) => {
          if (command === "startBattle") {
            setStartBattle(true);
            setPlayTime(1);
          }
          if (command === "newPlayerForBattle") {
            setNumberOfBattlePlayers((prevPlayers) => {
              return prevPlayers + 1;
            });
          }
        }
      );

      if (isDisposed) {
        if (connection) {
          socketService.stopConnection(connection);
        }
        return;
      }

      activeConnection = connection;
      inboxConnectionRef.current = connection;
      setInboxConnection(connection);
    };

    SetConnection().catch((error) => {
      console.error("Failed to set up SignalR connection:", error);
    });

    // Cleanup on unmount or logout
    return () => {
      isDisposed = true;
      if (activeConnection) {
        socketService.stopConnection(activeConnection);
        if (inboxConnectionRef.current === activeConnection) {
          inboxConnectionRef.current = null;
        }
      }
      setInboxConnection((previousConnection) => {
        if (previousConnection === activeConnection) {
          return null;
        }

        return previousConnection;
      });
      if (!activeConnection && inboxConnectionRef.current) {
        socketService.stopConnection(inboxConnectionRef.current);
        inboxConnectionRef.current = null;
        setInboxConnection(null);
      }
    };
  }, [currentUser?.uid, isPlayRoute, refreshBookingNotifications, refreshContactNotifications, refreshInvitationCount, refreshUnreadNotifications, refreshUserInfo, refreshNotificationHistory]);

  useEffect(() => {
    if (!currentUser?.uid) {
      setInvitationCount(0);
      setBookingNotificationCount(0);
      setNewContactUserIds([]);
      return;
    }

    if (isPlayRoute) {
      return;
    }

    refreshInvitationCount(currentUser.uid);
    refreshBookingNotifications(currentUser.uid);
    refreshContactNotifications();
  }, [currentUser?.uid, isPlayRoute, refreshBookingNotifications, refreshContactNotifications, refreshInvitationCount]);

  useEffect(() => {
    if (typeof window === "undefined" || isPlayRoute || !currentUser?.uid) {
      return;
    }

    const handleBookingUpdated = () => {
      refreshBookingNotifications(currentUser.uid);
    };

    window.addEventListener("memo:booking-updated", handleBookingUpdated);
    window.addEventListener("memo:booking-notifications-updated", handleBookingUpdated);

    return () => {
      window.removeEventListener("memo:booking-updated", handleBookingUpdated);
      window.removeEventListener("memo:booking-notifications-updated", handleBookingUpdated);
    };
  }, [currentUser?.uid, isPlayRoute, refreshBookingNotifications]);

  const toggleSideMenu = () => {
    setSideMenuOpen(!sideMenuOpen);
  };
  const toggleHeaderSerach = () => {
    setHeaderSerachOpen(!headerSerachOpen);
  };

  const contextValue: AppContextType = {
    sideMenuOpen,
    headerSerachOpen,
    toggleHeaderSerach,
    toggleSideMenu,
    scrollDirection,
    setScrollDirection,
    inputValue,
    setInputValue,
    filterType,
    setFilterType,
    confirmationPage,
    setConfirmationPage,
    currentUser,
    setCurrentUser,
    authResolved,
    userInfoResolved,
    sessionRevision,
    userInfo,
    setUserInfo,
    locale,
    setLocale,
    inboxConnection,
    setInboxConnection,
    playTime,
    setPlayTime,
    playAnswers,
    setPlayAnswers,
    playStartTime,
    setPlayStartTime,
    playEndTime,
    setPlayEndTime,
    newMessage,
    setNewMessage,
    unreadCount,
    setUnreadCount,
    resetUnreadCount,
    notificationHistory: ownNotificationProjection?.page ?? null,
    notificationUnreadCount: ownNotificationProjection?.page?.totalUnreadCount ?? 0,
    notificationHistoryError: ownNotificationProjection?.error ?? false,
    refreshNotificationHistory,
    readNotification,
    readAllNotifications,
    invitationCount,
    setInvitationCount,
    resetInvitationCount,
    bookingNotificationCount,
    setBookingNotificationCount,
    resetBookingNotificationCount,
    contactCount: newContactUserIds.length,
    resetContactCount,
    newContactUserIds,
    setNewContactUserIds,
    challengeCount,
    setChallengeCount,
    resetChallengeCount,
    battleScore,
    setBattleScore,
    numberOfBattlePlayers,
    setNumberOfBattlePlayers,
    startBattle,
    setStartBattle,
  };

  return (
    <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>
  );
};

export default AppProvider;
